import {
  VuexModule,
  Module,
  getModule,
  Action,
  Mutation,
} from 'vuex-module-decorators';

import store from '@/store';
import { ErrorAlertModule } from '@/store/modules/errors';
import {
  createEditedStatement,
  getEditedStatementInfo,
  getEditedStatementInfoToShow,
  checkEditedIsArchiveFormed,
  IsArchiveFormedResponseSuccess,
  IsArchiveFormedResponseError,
} from '@/api/editedStatement';
import { CommonObject, IAsyncState } from '@/utils/types';
import { IStatement, ICreateStatementResponse } from '@/types/services';

const typeGuardIsArchiveFormedResponseSuccess = (
  response: IsArchiveFormedResponseSuccess | IsArchiveFormedResponseError
): response is IsArchiveFormedResponseSuccess => {
  return (response as IsArchiveFormedResponseSuccess).formed !== undefined;
};

@Module({ dynamic: true, store, name: 'edited', namespaced: true })
class EditedStatement extends VuexModule {
  statement: IAsyncState<ICreateStatementResponse> = {
    entity: null,
    loaded: false,
    loading: false,
  };

  statementInfo: IAsyncState<IStatement> = {
    entity: null,
    loaded: false,
    loading: false,
  };

  statementInfoToShow: IAsyncState<CommonObject> = {
    entity: null,
    loaded: false,
    loading: false,
  };

  isArchiveFormed: IAsyncState<boolean | string> = {
    entity: false,
    loaded: false,
    loading: false,
  };

  @Mutation
  pendingCreateStatement() {
    ErrorAlertModule.setErrorAlert('');

    this.statement.entity = null;
    this.statement.loading = true;
    this.statement.loaded = false;
  }

  @Mutation
  fulfilledCreateStatement(payload: ICreateStatementResponse) {
    this.statement.entity = payload;
    this.statement.loading = false;
    this.statement.loaded = true;
  }

  @Mutation
  rejectedCreateStatement(payload: string) {
    this.statement.loading = false;
    ErrorAlertModule.setErrorAlert(payload);
  }

  @Mutation
  pendingGetStatementInfo() {
    ErrorAlertModule.setErrorAlert('');

    this.statementInfo.entity = null;
    this.statementInfo.loading = true;
    this.statementInfo.loaded = false;
  }

  @Mutation
  fulfilledGetStatementInfo(payload: IStatement) {
    this.statementInfo.entity = payload;
    this.statementInfo.loading = false;
    this.statementInfo.loaded = true;
  }

  @Mutation
  rejectedGetStatementInfo(payload: string) {
    this.statementInfo.loading = false;
    ErrorAlertModule.setErrorAlert(payload);
  }

  @Mutation
  pendingGetStatementInfoToShow() {
    ErrorAlertModule.setErrorAlert('');

    this.statementInfoToShow.entity = null;
    this.statementInfoToShow.loading = true;
    this.statementInfoToShow.loaded = false;
  }

  @Mutation
  fulfilledGetStatementInfoToShow(payload: CommonObject) {
    this.statementInfoToShow.entity = payload;
    this.statementInfoToShow.loading = false;
    this.statementInfoToShow.loaded = true;
  }

  @Mutation
  rejectedGetStatementInfoToShow(payload: string) {
    this.statementInfoToShow.loading = false;
    ErrorAlertModule.setErrorAlert(payload);
  }

  @Mutation
  pendingCheckIsArchiveFormed() {
    ErrorAlertModule.setErrorAlert('');

    this.isArchiveFormed.entity = false;
    this.isArchiveFormed.loading = true;
    this.isArchiveFormed.loaded = false;
  }

  @Mutation
  fulfilledCheckIsArchiveFormed(payload: boolean | string) {
    this.isArchiveFormed.entity = payload;
    this.isArchiveFormed.loading = false;
    this.isArchiveFormed.loaded = true;
  }

  @Mutation
  rejectedCheckIsArchiveFormed(payload: string) {
    this.isArchiveFormed.loading = false;
    ErrorAlertModule.setErrorAlert(payload);
  }

  @Action
  async createStatement(statement: IStatement) {
    this.pendingCreateStatement();

    createEditedStatement(statement)
      .then((data) => {
        this.fulfilledCreateStatement(data);
      })
      .catch((error) => {
        this.rejectedCreateStatement(error);
      });
  }

  @Action
  async getStatementInfo(statementId: string) {
    this.pendingGetStatementInfo();

    getEditedStatementInfo(statementId)
      .then((data) => {
        this.fulfilledGetStatementInfo(data);
      })
      .catch((error) => {
        this.rejectedGetStatementInfo(error);
      });
  }

  @Action
  async getStatementInfoToShow(statementId: string) {
    this.pendingGetStatementInfoToShow();

    getEditedStatementInfoToShow(statementId)
      .then((data) => {
        this.fulfilledGetStatementInfoToShow(data);
      })
      .catch((error) => {
        this.rejectedGetStatementInfoToShow(error);
      });
  }

  @Action
  async checkIsArchiveFormed(originalStatementId: string) {
    this.pendingCheckIsArchiveFormed();

    checkEditedIsArchiveFormed(originalStatementId)
      .then((data) => {
        if (typeGuardIsArchiveFormedResponseSuccess(data)) {
          this.fulfilledCheckIsArchiveFormed(data.formed);
        } else {
          this.fulfilledCheckIsArchiveFormed(data.errorString);
        }
      })
      .catch((error) => {
        this.rejectedCheckIsArchiveFormed(error);
      });
  }
}

export const EditedStatementModule = getModule(EditedStatement);
